import apiClient from "../utils/api";
import { API_BASE_URL } from "../config";

//------ AUTHENTICATION ------//

export const login = async (credentials) => {
  try {
    const response = await apiClient.post("/auth/login", credentials);
    return response.data;
  } catch (error) {
    console.error("Error during login:", error);
    throw error;
  }
};

export const register = async (userData) => {
  try {
    const response = await apiClient.post("/auth/register", userData);
    return response.data;
  } catch (error) {
    console.error("Error during registration:", error);
    throw error;
  }
};

// Fetch job types
export const getJobTypes = async () => {
  try {
    const response = await apiClient.get("/tipoTrabajo/all");
    return response.data;
  } catch (error) {
    console.error("Error fetching job types:", error);
    throw error;
  }
};

// Fetch companies (active only)
export const getCompanies = async () => {
  try {
    const response = await apiClient.get("/compania/getAllActivas");
    return response.data;
  } catch (error) {
    console.error("Error fetching companies:", error);
    throw error;
  }
};

// Fetch companies summary (all companies with summary info)
export const getCompaniesSummary = async () => {
  try {
    const response = await apiClient.get("/compania/resumen");
    return response.data;
  } catch (error) {
    console.error("Error fetching companies summary:", error);
    throw error;
  }
};

// Fetch commercials
export const getCommercials = async () => {
  try {
    const response = await apiClient.get("/comercial/all");
    return response.data;
  } catch (error) {
    console.error("Error fetching commercials:", error);
    throw error;
  }
};

// Fetch states
export const getStates = async () => {
  try {
    const response = await apiClient.get("/estado/all");
    return response.data;
  } catch (error) {
    console.error("Error fetching states:", error);
    throw error;
  }
};

// Fetch localities
export const getLocalities = async () => {
  try {
    const response = await apiClient.get("/localidad/all");
    return response.data;
  } catch (error) {
    console.error("Error fetching localities:", error);
    throw error;
  }
};

// Fetch budget notes
export const getBudgetNotes = async () => {
  try {
    const response = await apiClient.get("/notasPresupuesto/all");
    return response.data;
  } catch (error) {
    console.error("Error fetching budget notes:", error);
    throw error;
  }
};

// Fetch budget notes summary (for dropdowns)
export const getBudgetNotesSummary = async () => {
  try {
    const response = await apiClient.get("/notasPresupuesto/resumen");
    return response.data;
  } catch (error) {
    console.error("Error fetching budget notes summary:", error);
    throw error;
  }
};

// Get budget note by ID
export const getBudgetNoteById = async (noteId) => {
  try {
    const response = await apiClient.get(`/notasPresupuesto/findByID/${noteId}`);
    return response.data;
  } catch (error) {
    console.error("Error fetching budget note by ID:", error);
    throw error;
  }
};

// Edit budget note
export const editBudgetNote = async (noteId, noteData) => {
  try {
    const response = await apiClient.put(`/notasPresupuesto/editar/${noteId}`, noteData);
    return response.data;
  } catch (error) {
    console.error("Error editing budget note:", error);
    throw error;
  }
};

// Delete budget note
export const deleteBudgetNote = async (noteId) => {
  try {
    const response = await apiClient.delete(`/notasPresupuesto/eliminar/${noteId}`);
    return response.data;
  } catch (error) {
    console.error("Error deleting budget note:", error);
    throw error;
  }
};

//------ ALARMAS ------//

// Fetch all alarms summary by user ID
export const getAlarmsSummaryAllStates = async (usuarioId) => {
  try {
    const response = await apiClient.get("/expediente/resumenAlarmasAllEstados", {
      params: { usuarioId },
    });
    return response.data;
  } catch (error) {
    console.error(`Error fetching all alarms for user ${usuarioId}:`, error);
    throw error;
  }
};

// Fetch alarms summary by state
export const getAlarmsSummaryByState = async (estadoCodigo = "", usuarioId = "", grupoTrabajo = "") => {
  try {
    const response = await apiClient.get("/expediente/resumenAlarmasPorEstado", {
      params: { estadoCodigo, usuarioId, grupoTrabajo },
    });
    return response.data;
  } catch (error) {
    console.error("Error fetching alarms by state:", error);
    throw error;
  }
};

// Fetch alarms count by state
export const getAlarmsCountByState = async (usuarioId = "", grupoTrabajo = "") => {
  try {
    const response = await apiClient.get("/expediente/countAlarmasVencidas", {
      params: { usuarioId, grupoTrabajo },
    });
    return response.data;
  } catch (error) {
    console.error("Error fetching alarms count by state:", error);
    throw error;
  }
};

//------ BUSQUEDA AVANZADA FACTURAS ------//

// Advanced search for invoices
export const searchInvoicesAdvanced = async (searchParams) => {
  try {
    const response = await apiClient.get("/factura/buscar", {
      params: searchParams,
    });
    return response.data;
  } catch (error) {
    console.error("Error in advanced invoice search:", error);
    throw error;
  }
};

// Fetch active users
export const getActiveUsers = async () => {
  try {
    const response = await apiClient.get("/usuarios/getUsuariosActivos");
    return response.data;
  } catch (error) {
    console.error("Error fetching active users:", error);
    throw error;
  }
};

// Fetch workers
export const getWorkers = async () => {
  try {
    const response = await apiClient.get("/operario/resumen");
    return response.data;
  } catch (error) {
    console.error("Error fetching workers:", error);
    throw error;
  }
};

// Fetch active workers by guild
export const getActiveWorkersByGuild = async (guildId) => {
  try {
    const response = await apiClient.get(`/operario/activoByGremio/${guildId}`);
    return response.data;
  } catch (error) {
    console.error("Error fetching active workers by guild:", error);
    throw error;
  }
};

// Fetch calendar events for operarios
export const getCalendarAppointments = async ({ operarioIds, fromDate, toDate }) => {
  try {
    const params = {
      operarioIds: operarioIds && operarioIds.length > 0 ? operarioIds.join(",") : "",
      fromDate,
      toDate,
    };
    const response = await apiClient.get("/cita/calendar", { params });
    return response.data;
  } catch (error) {
    console.error("Error fetching calendar appointments:", error);
    throw error;
  }
};

// Fetch appointments by week range
export const getCitasCalendarioPorSemana = async ({ fromDate, toDate }) => {
  try {
    const params = {
      fromDate,
      toDate,
    };
    const response = await apiClient.get("/cita/citasCalendarioPorSemana", { params });
    return response.data;
  } catch (error) {
    console.error("Error fetching citas calendario por semana:", error);
    throw error;
  }
};

// Fetch default budgets
export const getDefaultBudgets = async () => {
  try {
    const response = await apiClient.get("/ptoPred/all");
    return response.data;
  } catch (error) {
    console.error("Error fetching default budgets:", error);
    throw error;
  }
};

// Fetch guilds
export const getGuilds = async () => {
  try {
    const response = await apiClient.get("/gremio/all");
    return response.data;
  } catch (error) {
    console.error("Error fetching guilds:", error);
    throw error;
  }
};

//------ BÚSQUEDA EN TABLAS ------//

// Search companies by name
export const searchCompaniesByName = async (nombre) => {
  try {
    const response = await apiClient.get(`/compania/buscar/${nombre}`);
    return response.data;
  } catch (error) {
    console.error("Error searching companies by name:", error);
    throw error;
  }
};

// Search operators by name
export const searchOperatorsByName = async (nombre) => {
  try {
    const response = await apiClient.get(`/operario/buscar/${nombre}`);
    return response.data;
  } catch (error) {
    console.error("Error searching operators by name:", error);
    throw error;
  }
};

// Search predetermined budgets by name
export const searchPredeterminedBudgetsByName = async (nombre) => {
  try {
    const response = await apiClient.get(`/ptoPred/buscar/${nombre}`);
    return response.data;
  } catch (error) {
    console.error("Error searching predetermined budgets by name:", error);
    throw error;
  }
};

// Search budget notes by name
export const searchBudgetNotesByName = async (nombre) => {
  try {
    const response = await apiClient.get(`/notasPresupuesto/buscar/${nombre}`);
    return response.data;
  } catch (error) {
    console.error("Error searching budget notes by name:", error);
    throw error;
  }
};

//------ RECORDS ------//

// Fetch records summary by user (or all if no user)
export const getRecords = async (usuarioId) => {
  try {
    const response = await apiClient.get("/expediente/resumenAllEstados", {
      params: usuarioId ? { usuarioId } : {},
    });
    return response.data;
  } catch (error) {
    console.error("Error fetching records:", error);
    throw error;
  }
};

// Create a new record
export const createRecord = async (recordData) => {
  try {
    const response = await apiClient.post("/expediente/crear", recordData);
    return response.data;
  } catch (error) {
    console.error("Error creating record:", error);
    throw error;
  }
};

// Edit an existing record
export const editRecord = async (expedienteId, recordData) => {
  try {
    const response = await apiClient.put(`/expediente/editar/${expedienteId}`, recordData);
    return response.data;
  } catch (error) {
    console.error("Error editing record:", error);
    throw error;
  }
};

// Fetch records count by state
export const getRecordsCountByState = async (usuarioId = "", grupoTrabajo = "") => {
  try {
    const response = await apiClient.get("/expediente/countByEstado", {
      params: { usuarioId, grupoTrabajo },
    });
    return response.data;
  } catch (error) {
    console.error("Error fetching records count by state:", error);
    throw error;
  }
};

// Fetch records summary by state
export const getRecordsByState = async (estadoCodigo = "", usuarioId = "", grupoTrabajo = "") => {
  try {
    const response = await apiClient.get("/expediente/resumenPorEstado", {
      params: { estadoCodigo, usuarioId, grupoTrabajo },
    });
    return response.data;
  } catch (error) {
    console.error("Error fetching records by state:", error);
    throw error;
  }
};

// Fetch record by ID
export const getRecordById = async (id) => {
  try {
    const response = await apiClient.get(`/expediente/findByID/${id}`);
    return response.data;
  } catch (error) {
    console.error(`Error fetching record with ID ${id}:`, error);
    throw error;
  }
};

// Fetch record statistics by ID
export const getRecordStatistics = async (expedienteId) => {
  try {
    const response = await apiClient.get(`/expediente/estadistica/${expedienteId}`);
    return response.data;
  } catch (error) {
    console.error(`Error fetching record statistics for expedienteId ${expedienteId}:`, error);
    throw error;
  }
};

// Fetch contacts by expediente ID
export const getContactsByExpedienteId = async (expedienteId) => {
  try {
    const response = await apiClient.get(`/expediente/getContactosByExpedienteId/${expedienteId}`);
    return response.data;
  } catch (error) {
    console.error(`Error fetching contacts for expedienteId ${expedienteId}:`, error);
    throw error;
  }
};

// Fetch non-expediente contacts (for calls)
export const getNonExpedienteContacts = async (searchQuery = "") => {
  try {
    const response = await apiClient.get("/contacto/no-expedientes", {
      params: searchQuery ? { q: searchQuery } : {},
    });
    return response.data;
  } catch (error) {
    console.error("Error fetching non-expediente contacts:", error);
    throw error;
  }
};

//------ INDEPENDENT CONTACTS ------//

// Fetch all independent contacts
export const getIndependentContacts = async () => {
  try {
    const response = await apiClient.get("/contacto/independientes");
    return response.data;
  } catch (error) {
    console.error("Error fetching independent contacts:", error);
    throw error;
  }
};

// Search independent contacts by query
export const searchIndependentContacts = async (q = "") => {
  try {
    const params = {};
    if (q && q.trim()) params.q = q;
    
    const response = await apiClient.get("/contacto/independiente/buscar", {
      params
    });
    return response.data;
  } catch (error) {
    console.error("Error searching independent contacts:", error);
    throw error;
  }
};

// Create new independent contact
export const createIndependentContact = async (contactData) => {
  try {
    const response = await apiClient.post("/contacto/independiente/crear", contactData);
    return response.data;
  } catch (error) {
    console.error("Error creating independent contact:", error);
    throw error;
  }
};

// Update existing independent contact
export const updateIndependentContact = async (contactId, contactData) => {
  try {
    const response = await apiClient.put(`/contacto/independiente/editar/${contactId}`, contactData);
    return response.data;
  } catch (error) {
    console.error("Error updating independent contact:", error);
    throw error;
  }
};

// Delete independent contact
export const deleteIndependentContact = async (contactId) => {
  try {
    const response = await apiClient.delete(`/contacto/independiente/eliminar/${contactId}`);
    return response.data;
  } catch (error) {
    console.error("Error deleting independent contact:", error);
    throw error;
  }
};

//PRESUPUESTO

// Fetch budget summary by expediente ID
export const getBudgetSummary = async (expedienteId) => {
  try {
    const response = await apiClient.get("/presupuesto/resumen", {
      params: { expedienteId },
    });
    return response.data; // [{fecha, asunto, numPresupuesto, ...}]
  } catch (error) {
    console.error(`Error fetching budget summary for expedienteId ${expedienteId}:`, error);
    throw error;
  }
};

// Fetch budget by ID
export const getBudgetById = async (budgetId) => {
  try {
    const response = await apiClient.get(`/presupuesto/findByID/${budgetId}`);
    return response.data;
  } catch (error) {
    console.error(`Error fetching budget with ID ${budgetId}:`, error);
    throw error;
  }
};

// Edit budget by ID
export const editBudget = async (budgetId, budgetData) => {
  try {
    const response = await apiClient.post(`/presupuesto/editar/${budgetId}`, budgetData);
    return response.data;
  } catch (error) {
    console.error(`Error editing budget with ID ${budgetId}:`, error);
    throw error;
  }
};

// Generate budget PDF - returns the file path
export const generateBudgetPDF = async (budgetId) => {
  try {
    const response = await apiClient.get(`/presupuesto/crearPresupuestoPDF/${budgetId}`);
    return response.data; // Devuelve la ruta del archivo: //10.10.0.1/shared/Expedientes/...
  } catch (error) {
    console.error(`Error generating PDF for budget ID ${budgetId}:`, error);
    throw error;
  }
};

// Send budget - uses the same email API as replyEmail
export const sendBudget = async (budgetId, sendData) => {
  try {
    const usuarioId = localStorage.getItem("usuarioId");
    
    if (!usuarioId) {
      throw new Error("No se pudo obtener el ID del usuario");
    }

    // Construir el body con rutas SAMBA (strings)
    const emailData = {
      descripcion: sendData.subject || '',
      mensaje: sendData.information || '',
      usuarioId: parseInt(usuarioId),
      files: sendData.attachments || [] // Array de rutas SAMBA: ["/srv/samba/shared/Expedientes/2025/255133/prueba/file.jpg"]
    };

    // Solo agregar gmailMessageId si existe
    if (sendData.gmailMessageId) {
      emailData.gmailMessageId = sendData.gmailMessageId;
    }

    // Agregar to, cc y bcc
    if (sendData.to) {
      emailData.to = sendData.to;
    }
    if (sendData.cc) {
      emailData.cc = sendData.cc;
    }
    if (sendData.bcc) {
      emailData.bcc = sendData.bcc;
    }

    console.log('Sending budget email:', emailData);

    const response = await apiClient.post("/anotacion/responderCorreo", emailData);
    return response.data;
  } catch (error) {
    console.error(`Error sending budget ID ${budgetId}:`, error);
    throw error;
  }
};

// Send invoice - uses the same email API as replyEmail
export const sendInvoice = async (invoiceId, sendData) => {
  try {
    const usuarioId = localStorage.getItem("usuarioId");
    
    if (!usuarioId) {
      throw new Error("No se pudo obtener el ID del usuario");
    }

    // Construir el body con rutas SAMBA (strings)
    const emailData = {
      descripcion: sendData.subject || '',
      mensaje: sendData.information || '',
      usuarioId: parseInt(usuarioId),
      files: sendData.attachments || [] // Array de rutas SAMBA: ["/srv/samba/shared/Expedientes/2025/255133/prueba/file.jpg"]
    };

    // Solo agregar gmailMessageId si existe
    if (sendData.gmailMessageId) {
      emailData.gmailMessageId = sendData.gmailMessageId;
    }

    // Agregar to, cc y bcc
    if (sendData.to) {
      emailData.to = sendData.to;
    }
    if (sendData.cc) {
      emailData.cc = sendData.cc;
    }
    if (sendData.bcc) {
      emailData.bcc = sendData.bcc;
    }

    console.log('Sending invoice email:', emailData);

    const response = await apiClient.post("/anotacion/responderCorreo", emailData);
    return response.data;
  } catch (error) {
    console.error(`Error sending invoice ID ${invoiceId}:`, error);
    throw error;
  }
};

// Send appointment - uses the same email API as replyEmail
export const sendAppointment = async (appointmentId, sendData) => {
  try {
    const usuarioId = localStorage.getItem("usuarioId");
    
    if (!usuarioId) {
      throw new Error("No se pudo obtener el ID del usuario");
    }

    // Construir el body con rutas SAMBA (strings)
    const emailData = {
      descripcion: sendData.subject || '',
      mensaje: sendData.information || '',
      usuarioId: parseInt(usuarioId),
      files: sendData.attachments || [] // Array de rutas SAMBA: ["/srv/samba/shared/Expedientes/2025/255133/prueba/file.jpg"]
    };

    // Solo agregar gmailMessageId si existe
    if (sendData.gmailMessageId) {
      emailData.gmailMessageId = sendData.gmailMessageId;
    }

    // Agregar to, cc y bcc
    if (sendData.to) {
      emailData.to = sendData.to;
    }
    if (sendData.cc) {
      emailData.cc = sendData.cc;
    }
    if (sendData.bcc) {
      emailData.bcc = sendData.bcc;
    }

    console.log('Sending appointment email:', emailData);

    const response = await apiClient.post("/anotacion/responderCorreo", emailData);
    return response.data;
  } catch (error) {
    console.error(`Error sending appointment ID ${appointmentId}:`, error);
    throw error;
  }
};

// Get company billing email by expediente ID
export const getCompanyBillingEmail = async (expedienteId) => {
  try {
    const response = await apiClient.get(`/expediente/correoFacturacionCompania/${expedienteId}`);
    return response.data;
  } catch (error) {
    console.error(`Error fetching billing email for expediente ${expedienteId}:`, error);
    throw error;
  }
};

// Fetch budget lines summary by expediente ID
export const getBudgetLinesSummary = async (expedienteId) => {
  try {
    const response = await apiClient.get("/lineaPresupuesto/resumen", {
      params: { expedienteId },
    });
    return response.data;
  } catch (error) {
    console.error(`Error fetching budget lines summary for expedienteId ${expedienteId}:`, error);
    throw error;
  }
};

// Create budget line
export const createBudgetLine = async (budgetLineData) => {
  try {
    const response = await apiClient.post("/lineaPresupuesto/crear", budgetLineData);
    return response.data;
  } catch (error) {
    console.error("Error creating budget line:", error);
    throw error;
  }
};

// Get budget line details by ID
export const getBudgetLineById = async (id) => {
  try {
    const response = await apiClient.get(`/lineaPresupuesto/findByID/${id}`);
    return response.data;
  } catch (error) {
    console.error(`Error fetching budget line details for ID ${id}:`, error);
    throw error;
  }
};

// Edit budget line by ID
export const editBudgetLine = async (id, budgetLineData) => {
  try {
    const response = await apiClient.put(`/lineaPresupuesto/editar/${id}`, budgetLineData);
    return response.data;
  } catch (error) {
    console.error(`Error editing budget line with ID ${id}:`, error);
    throw error;
  }
};

// Get budget line data for appointment (DTO)
export const getBudgetLineForAppointment = async (lineaPresupuestoId) => {
  try {
    const response = await apiClient.get(`/lineaPresupuesto/getLineaPresupuestoParaCitaDTO/${lineaPresupuestoId}`);
    return response.data;
  } catch (error) {
    console.error(`Error fetching budget line for appointment with ID ${lineaPresupuestoId}:`, error);
    throw error;
  }
};

// Fetch rConjuntos by expediente ID
export const getRConjuntos = async (expedienteId) => {
  try {
    const response = await apiClient.get("/expediente/rConjuntos", {
      params: { expedienteId },
    });
    return response.data; // [adminCommission, rConjuntoMin, rConjuntoMax] - ej: [5.0, 0.65, 0.63]
  } catch (error) {
    console.error(`Error fetching rConjuntos for expedienteId ${expedienteId}:`, error);
    throw error;
  }
};

//CITAS

// Fetch appointment summary by expediente ID (NUEVA RESPUESTA)
export const getAppointmentSummary = async (expedienteId) => {
  try {
    const response = await apiClient.get("/cita/resumen", {
      params: { expedienteId },
    });
    return response.data; // [{fechaCita, fechaCitaFin, numPresupuesto, costeTotal, gremio, operarioNombre, id}]
  } catch (error) {
    console.error(`Error fetching appointment summary for expedienteId ${expedienteId}:`, error);
    throw error;
  }
};

// Fetch appointment by ID
export const getAppointmentById = async (id) => {
  try {
    const response = await apiClient.get(`/cita/findByID/${id}`);
    return response.data;
  } catch (error) {
    console.error(`Error fetching appointment with ID ${id}:`, error);
    throw error;
  }
};

export const createAppointment = async (appointmentData) => {
  try {
    const response = await apiClient.post("/cita/crear", appointmentData);
    return response.data;
  } catch (error) {
    console.error("Error creating appointment:", error);
    throw error;
  }
};

// Update appointment
export const updateAppointment = async (appointmentId, appointmentData) => {
  try {
    const response = await apiClient.put(`/cita/editar/${appointmentId}`, appointmentData);
    return response.data;
  } catch (error) {
    console.error("Error updating appointment:", error);
    throw error;
  }
};

// Assign appointment to user
export const assignAppointment = async (appointmentId, usuarioId, urgente = false) => {
  try {
    const response = await apiClient.post(
      `/cita/asignarCita/${appointmentId}?usuarioId=${usuarioId}&urgente=${urgente}`
    );
    return response.data;
  } catch (error) {
    console.error("Error assigning appointment:", error);
    throw error;
  }
};

// Get compact annotations summary
export const getCompactAnnotationsSummary = async (expedienteId) => {
  try {
    const response = await apiClient.get(`/anotacion/resumenCompacto/${expedienteId}`);
    return response.data;
  } catch (error) {
    console.error("Error fetching compact annotations summary:", error);
    throw error;
  }
};

// Get predefined appointment text
export const getPredefinedAppointmentText = async (appointmentId) => {
  try {
    const response = await apiClient.get(`/cita/citaPredefinido/${appointmentId}`);
    return response.data;
  } catch (error) {
    console.error("Error fetching predefined appointment text:", error);
    throw error;
  }
};

// Get predefined budget text
export const getPredefinedBudgetText = async () => {
  try {
    const response = await apiClient.get('/anotacion/presupuestoPredefinido');
    return response.data;
  } catch (error) {
    console.error("Error fetching predefined budget text:", error);
    throw error;
  }
};

// Get predefined invoice text
export const getPredefinedInvoiceText = async () => {
  try {
    const response = await apiClient.get('/anotacion/facturaPredefinido');
    return response.data;
  } catch (error) {
    console.error("Error fetching predefined invoice text:", error);
    throw error;
  }
};

// Get predefined email reply text
export const getPredefinedEmailText = async () => {
  try {
    const response = await apiClient.get('/anotacion/correoPredefinido');
    return response.data;
  } catch (error) {
    console.error("Error fetching predefined email text:", error);
    throw error;
  }
};

//------ OPERARIOS ------//

// Create operator/worker
export const createOperator = async (operatorData) => {
  try {
    const response = await apiClient.post("/operario/crear", operatorData);
    return response.data;
  } catch (error) {
    console.error("Error creating operator:", error);
    throw error;
  }
};

// Get operator details by ID
export const getOperatorById = async (id) => {
  try {
    const response = await apiClient.get(`/operario/findByID/${id}`);
    return response.data;
  } catch (error) {
    console.error(`Error fetching operator with ID ${id}:`, error);
    throw error;
  }
};

// Edit operator
export const editOperator = async (id, operatorData) => {
  try {
    const response = await apiClient.put(`/operario/editar/${id}`, operatorData);
    return response.data;
  } catch (error) {
    console.error(`Error editing operator with ID ${id}:`, error);
    throw error;
  }
};

// Delete operator (preparado para cuando esté la API)
export const deleteOperator = async (id) => {
  try {
    const response = await apiClient.delete(`/operario/delete/${id}`);
    return response.data;
  } catch (error) {
    console.error(`Error deleting operator with ID ${id}:`, error);
    throw error;
  }
};

//------ COMPAÑÍAS ------//

// Create company
export const createCompany = async (companyData) => {
  try {
    const response = await apiClient.post("/compania/crear", companyData);
    return response.data;
  } catch (error) {
    console.error("Error creating company:", error);
    throw error;
  }
};

// Get company details by ID
export const getCompanyById = async (id) => {
  try {
    const response = await apiClient.get(`/compania/findByID/${id}`);
    return response.data;
  } catch (error) {
    console.error(`Error fetching company with ID ${id}:`, error);
    throw error;
  }
};

// Edit company
export const editCompany = async (id, companyData) => {
  try {
    const response = await apiClient.put(`/compania/editar/${id}`, companyData);
    return response.data;
  } catch (error) {
    console.error(`Error editing company with ID ${id}:`, error);
    throw error;
  }
};

// Delete company (preparado para cuando esté la API)
export const deleteCompany = async (id) => {
  try {
    const response = await apiClient.delete(`/compania/delete/${id}`);
    return response.data;
  } catch (error) {
    console.error(`Error deleting company with ID ${id}:`, error);
    throw error;
  }
};

//------ PRESUPUESTOS PREDETERMINADOS ------//

// Create predetermined budget
export const createPredeterminedBudget = async (budgetData) => {
  try {
    const response = await apiClient.post("/ptoPred/crear", budgetData);
    return response.data;
  } catch (error) {
    console.error("Error creating predetermined budget:", error);
    throw error;
  }
};

// Get predetermined budget by ID
export const getPredeterminedBudgetById = async (budgetId) => {
  try {
    const response = await apiClient.get(`/ptoPred/findByID/${budgetId}`);
    return response.data;
  } catch (error) {
    console.error("Error fetching predetermined budget by ID:", error);
    throw error;
  }
};

// Edit predetermined budget
export const editPredeterminedBudget = async (budgetId, budgetData) => {
  try {
    const response = await apiClient.put(`/ptoPred/editar/${budgetId}`, budgetData);
    return response.data;
  } catch (error) {
    console.error("Error editing predetermined budget:", error);
    throw error;
  }
};

// Delete predetermined budget
export const deletePredeterminedBudget = async (budgetId) => {
  try {
    const response = await apiClient.delete(`/ptoPred/eliminar/${budgetId}`);
    return response.data;
  } catch (error) {
    console.error("Error deleting predetermined budget:", error);
    throw error;
  }
};

//------ COMERCIALES ------//

// Create commercial
export const createCommercial = async (commercialData) => {
  try {
    const response = await apiClient.post("/comercial/crear", commercialData);
    return response.data;
  } catch (error) {
    console.error("Error creating commercial:", error);
    throw error;
  }
};

// Get commercial by ID
export const getCommercialById = async (commercialId) => {
  try {
    const response = await apiClient.get(`/comercial/findByID/${commercialId}`);
    return response.data;
  } catch (error) {
    console.error("Error fetching commercial by ID:", error);
    throw error;
  }
};

// Edit commercial
export const editCommercial = async (commercialId, commercialData) => {
  try {
    const response = await apiClient.put(`/comercial/editar/${commercialId}`, commercialData);
    return response.data;
  } catch (error) {
    console.error("Error editing commercial:", error);
    throw error;
  }
};

// Delete commercial
export const deleteCommercial = async (commercialId) => {
  try {
    const response = await apiClient.delete(`/comercial/eliminar/${commercialId}`);
    return response.data;
  } catch (error) {
    console.error("Error deleting commercial:", error);
    throw error;
  }
};

//------ GREMIOS ------//

// Create guild
export const createGuild = async (guildData) => {
  try {
    const response = await apiClient.post("/gremio/crear", guildData);
    return response.data;
  } catch (error) {
    console.error("Error creating guild:", error);
    throw error;
  }
};

// Edit guild
export const editGuild = async (guildId, guildData) => {
  try {
    const response = await apiClient.put(`/gremio/editar/${guildId}`, guildData);
    return response.data;
  } catch (error) {
    console.error("Error editing guild:", error);
    throw error;
  }
};

//------ GMAIL CUT MARKERS ------//

// Get all Gmail cut markers
export const getGmailCutMarkers = async () => {
  try {
    const response = await apiClient.get("/gmail/cut-markers");
    return response.data;
  } catch (error) {
    console.error("Error fetching Gmail cut markers:", error);
    throw error;
  }
};

// Create Gmail cut marker
export const createGmailCutMarker = async (markerData) => {
  try {
    const response = await apiClient.post("/gmail/cut-markers", markerData);
    return response.data;
  } catch (error) {
    console.error("Error creating Gmail cut marker:", error);
    throw error;
  }
};

// Edit Gmail cut marker
export const editGmailCutMarker = async (markerId, markerData) => {
  try {
    const response = await apiClient.put(`/gmail/cut-markers/${markerId}`, markerData);
    return response.data;
  } catch (error) {
    console.error("Error editing Gmail cut marker:", error);
    throw error;
  }
};

// Delete Gmail cut marker
export const deleteGmailCutMarker = async (markerId) => {
  try {
    const response = await apiClient.delete(`/gmail/cut-markers/${markerId}`);
    return response.data;
  } catch (error) {
    console.error("Error deleting Gmail cut marker:", error);
    throw error;
  }
};

//------ NOTAS DE PRESUPUESTO ------//

// Create budget note
export const createBudgetNote = async (budgetNoteData) => {
  try {
    const response = await apiClient.post("/notasPresupuesto/crear", budgetNoteData);
    return response.data;
  } catch (error) {
    console.error("Error creating budget note:", error);
    throw error;
  }
};

//------ BÚSQUEDA AVANZADA ------//

// Advanced search for records
export const searchRecords = async (searchParams) => {
  try {
    const response = await apiClient.get("/expediente/buscar", {
      params: searchParams
    });
    return response.data;
  } catch (error) {
    console.error("Error searching records:", error);
    throw error;
  }
};

//------ RECORDATORIOS ------//

// Fetch reminders summary by expediente ID
export const getRemindersSummary = async (expedienteId) => {
  try {
    const response = await apiClient.get("/recordatorio/resumen", {
      params: { expedienteId },
    });
    return response.data;
  } catch (error) {
    console.error(`Error fetching reminders summary for expedienteId ${expedienteId}:`, error);
    throw error;
  }
};

// Create reminder
export const createReminder = async (reminderData) => {
  try {
    console.log("API createReminder called with data:", reminderData);
    const response = await apiClient.post("/recordatorio/crear", reminderData);
    console.log("API createReminder response:", response.data);
    return response.data;
  } catch (error) {
    console.error("Error creating reminder:", error);
    if (error.response) {
      console.error("Response data:", error.response.data);
      console.error("Response status:", error.response.status);
      console.error("Response headers:", error.response.headers);
    }
    throw error;
  }
};

// Update reminder
export const updateReminder = async (reminderId, reminderData) => {
  try {
    const response = await apiClient.put(`/recordatorio/editar/${reminderId}`, reminderData);
    return response.data;
  } catch (error) {
    console.error("Error updating reminder:", error);
    throw error;
  }
};

// Delete reminder
export const deleteReminder = async (reminderId) => {
  try {
    const response = await apiClient.delete(`/recordatorio/delete/${reminderId}`);
    return response.data;
  } catch (error) {
    console.error("Error deleting reminder:", error);
    throw error;
  }
};

//------ CITAS MANAGER/ADMIN ------//

// Fetch appointment counts by group (for the checkboxes section)
export const getAppointmentCounts = async (grupo) => {
  try {
    const response = await apiClient.get("/cita/resumenCitasRecibidas", {
      params: { grupo },
    });
    return response.data; // { parteVisita: 0, parteTrabajo: 4, parteReclamacion: 1, parteMediciones: 0, parteFotos: 0 }
  } catch (error) {
    console.error("Error fetching appointment counts:", error);
    throw error;
  }
};

// Fetch appointments by type and group for the table
export const getAppointmentsByTypeAndGroup = async (tipoCita, grupo) => {
  try {
    const response = await apiClient.get("/cita/resumenCitasPorTipoYGrupo", {
      params: { tipoCita, grupo },
    });
    return response.data; // Array of appointment objects
  } catch (error) {
    console.error("Error fetching appointments by type and group:", error);
    throw error;
  }
};

// Get active users with their assigned partes count
export const getUsersActivosAndPartes = async () => {
  try {
    const response = await apiClient.get("/cita/getUsuariosActivosAndPartes");
    return response.data; // Array of { usuarioId, nombre, partesAsignadas }
  } catch (error) {
    console.error("Error fetching users with partes count:", error);
    throw error;
  }
};

//------ USER PROFILE AND MANAGEMENT ------//

// Edit user profile (for current user)
export const editUser = async (id, userData) => {
  try {
    const response = await apiClient.put(`/auth/editarUsuario/${id}`, userData);
    return response.data;
  } catch (error) {
    console.error("Error editing user:", error);
    throw error;
  }
};

// Change user password
export const changePassword = async (passwordData) => {
  try {
    const response = await apiClient.put("/auth/changePassword", passwordData);
    return response.data;
  } catch (error) {
    console.error("Error changing password:", error);
    throw error;
  }
};

// Change user password by admin
export const changePasswordAdmin = async (userId, passwordData) => {
  try {
    const response = await apiClient.put(`/auth/changePasswordAdmin/${userId}`, passwordData);
    return response.data;
  } catch (error) {
    console.error("Error changing password (admin):", error);
    throw error;
  }
};

// Edit user by admin
export const editUserAdmin = async (id, userData) => {
  try {
    const response = await apiClient.put(`/auth/editarUsuarioAdmin/${id}`, userData);
    return response.data;
  } catch (error) {
    console.error("Error editing user (admin):", error);
    throw error;
  }
};

// Get users for admin table
export const getUsersAdmin = async (page = 0, size = 20) => {
  try {
    const response = await apiClient.get("/usuarios/getUsuariosAdmin", {
      params: { page, size }
    });
    return response.data;
  } catch (error) {
    console.error("Error fetching users (admin):", error);
    throw error;
  }
};

//------ INVOICES ------//

// Get invoice summary for expediente
export const getInvoiceSummary = async (expedienteId) => {
  try {
    const response = await apiClient.get(`/factura/resumen?expedienteId=${expedienteId}`);
    return response.data;
  } catch (error) {
    console.error("Error fetching invoice summary:", error);
    throw error;
  }
};

// Get invoice by ID (works for all types: Factura, Pre-factura, Rectificativa)
export const getInvoiceById = async (invoiceId) => {
  try {
    const response = await apiClient.get(`/factura/findByID/${invoiceId}`);
    return response.data;
  } catch (error) {
    console.error(`Error fetching invoice with ID ${invoiceId}:`, error);
    throw error;
  }
};

// Get VeriFactu incidents. Incidencia is a Java record serialized with camelCase fields.
export const getVerifactuIncidencias = async () => {
  try {
    const response = await apiClient.get("/factura/verifactu/incidencias");
    return Array.isArray(response.data) ? response.data : [];
  } catch (error) {
    console.error("Error fetching VeriFactu incidents:", error);
    throw error;
  }
};

// Submit corrected billing details for a rejected or accepted-with-errors invoice.
export const subsanarFactura = async (invoiceId, datos) => {
  try {
    const response = await apiClient.post(`/factura/subsanar/factura/${invoiceId}`, {
      nombreFacturacion: datos.nombreFacturacion,
      domicilioFacturacion: datos.domicilioFacturacion,
      localidadFacturacionId: datos.localidadFacturacionId,
      nifFacturacion: datos.nifFacturacion,
    });
    return response.data;
  } catch (error) {
    console.error(`Error subsanando factura ${invoiceId}:`, error);
    throw error;
  }
};

// Get budget data for invoice
export const getBudgetForInvoice = async (budgetId) => {
  try {
    const response = await apiClient.get(`/presupuesto/paraFactura/${budgetId}`);
    return response.data;
  } catch (error) {
    console.error(`Error fetching budget for invoice with ID ${budgetId}:`, error);
    throw error;
  }
};

// Create factura from prefactura (with optional data)
export const createFacturaFromPrefactura = async (idPrefactura, facturaData = null) => {
  try {
    // LOG: endpoint y payload
    try {
      console.log('[API LOG] POST', `/factura/crearByPrefactura/${idPrefactura}`, facturaData ? { body: facturaData } : { body: null });
    } catch (logErr) {
      console.log('[API LOG] POST /factura/crearByPrefactura/', idPrefactura);
    }

    const response = facturaData 
      ? await apiClient.post(`/factura/crearByPrefactura/${idPrefactura}`, facturaData)
      : await apiClient.post(`/factura/crearByPrefactura/${idPrefactura}`);
    return response.data;
  } catch (error) {
    console.error("Error creating factura from prefactura:", error);
    throw error;
  }
};

// Delete prefactura
export const deletePrefactura = async (idPrefactura) => {
  try {
    const response = await apiClient.delete(`/prefactura/delete/${idPrefactura}`);
    return response.data;
  } catch (error) {
    console.error("Error deleting prefactura:", error);
    throw error;
  }
};

// Generate PDF for factura
// Returns: http://localhost:8765/open?file=//10.10.0.1/shared/Expedientes/2025/253802/Facturas/Factura1.docx
export const generateFacturaPDF = async (idFactura) => {
  try {
    const response = await apiClient.get(`/factura/crearFacturaPDF/${idFactura}`, {});
    return response.data; // Devuelve la URL del WordLauncher
  } catch (error) {
    console.error(`Error generating PDF for factura ${idFactura}:`, error);
    throw error;
  }
};

// Generate PDF for prefactura
export const generatePrefacturaPDF = async (idPrefactura) => {
  try {
    const response = await apiClient.get(`/prefactura/crearPrfacturaPDF/${idPrefactura}`, {});
    return response.data;
  } catch (error) {
    console.error(`Error generating PDF for prefactura ${idPrefactura}:`, error);
    throw error;
  }
};

// Generate PDF for factura rectificativa
export const generateFacturaRectificativaPDF = async (idFacturaRectificativa) => {
  try {
    const response = await apiClient.get(`/facturaRectificativa/crearFacturaRectificativaPDF/${idFacturaRectificativa}`, {});
    return response.data;
  } catch (error) {
    console.error(`Error generating PDF for factura rectificativa ${idFacturaRectificativa}:`, error);
    throw error;
  }
};

// Change presupuesto IVA
export const changePresupuestoIva = async (presupuestoId, iva) => {
  try {
    const response = await apiClient.post(`/presupuesto/canviarIva/${presupuestoId}?iva=${encodeURIComponent(iva)}`);
    return response.data;
  } catch (error) {
    console.error("Error changing presupuesto IVA:", error);
    throw error;
  }
};

// Delete factura
export const deleteFactura = async (idFactura) => {
  try {
    const response = await apiClient.delete(`/factura/delete/${idFactura}`);
    return response.data;
  } catch (error) {
    console.error("Error deleting factura:", error);
    throw error;
  }
};

// Get prefactura by ID
export const getPrefacturaById = async (idPrefactura) => {
  try {
    const response = await apiClient.get(`/prefactura/findByID/${idPrefactura}`);
    return response.data;
  } catch (error) {
    console.error("Error fetching prefactura by ID:", error);
    throw error;
  }
};

// Create rectificativa
export const createFacturaRectificativa = async (idFactura, motivo) => {
  try {
    const response = await apiClient.post(`/facturaRectificativa/crear/${idFactura}?motivo=${encodeURIComponent(motivo)}`);
    return response.data;
  } catch (error) {
    console.error("Error creating factura rectificativa:", error);
    throw error;
  }
};

// Get factura rectificativa by ID
export const getRectificativaById = async (idRectificativa) => {
  try {
    const response = await apiClient.get(`/facturaRectificativa/findByID/${idRectificativa}`);
    return response.data;
  } catch (error) {
    console.error("Error fetching factura rectificativa by ID:", error);
    throw error;
  }
};

// Create factura
export const createFactura = async (facturaData) => {
  try {
    try {
      console.log('[API LOG] POST', '/factura/crear', { body: facturaData });
    } catch (logErr) {
      console.log('[API LOG] POST /factura/crear');
    }
    const response = await apiClient.post('/factura/crear', facturaData);
    return response.data;
  } catch (error) {
    console.error("Error creating factura:", error);
    throw error;
  }
};

// Create prefactura
export const createPrefactura = async (prefacturaData) => {
  try {
    try {
      console.log('[API LOG] POST', '/prefactura/crear', { body: prefacturaData });
    } catch (logErr) {
      console.log('[API LOG] POST /prefactura/crear');
    }
    const response = await apiClient.post('/prefactura/crear', prefacturaData);
    return response.data;
  } catch (error) {
    console.error("Error creating prefactura:", error);
    throw error;
  }
};

// Edit prefactura
export const editPrefactura = async (prefacturaId, prefacturaData) => {
  try {
    try {
      console.log('[API LOG] PUT', `/prefactura/editar/${prefacturaId}`, { body: prefacturaData });
    } catch (logErr) {
      console.log('[API LOG] PUT /prefactura/editar/', prefacturaId);
    }
    const response = await apiClient.put(`/prefactura/editar/${prefacturaId}`, prefacturaData);
    return response.data;
  } catch (error) {
    console.error("Error editing prefactura:", error);
    throw error;
  }
};

// Get budget lines for invoice
export const getBudgetLinesForInvoice = async (idPresupuesto) => {
  try {
    const response = await apiClient.get(`/presupuesto/paraFactura/${idPresupuesto}`);
    return response.data;
  } catch (error) {
    console.error("Error getting budget lines for invoice:", error);
    throw error;
  }
};

// Change tipo de cobro for factura
// Editar factura (IVA, tipo cobro, datos de facturación) - solo mismo mes
export const editarFactura = async (idFactura, data) => {
  try {
    const response = await apiClient.put(`/factura/editar/${idFactura}`, data);
    return response.data;
  } catch (error) {
    console.error("Error editing factura:", error);
    throw error;
  }
};

export const changeFacturaTipoCobro = async (idFactura, tipoCobro) => {
  try {
    const response = await apiClient.post(`/factura/cambiar-tipo-cobro/${idFactura}?tipoCobro=${encodeURIComponent(tipoCobro)}`);
    return response.data;
  } catch (error) {
    console.error("Error changing factura tipo de cobro:", error);
    throw error;
  }
};

// Change tipo de cobro for factura rectificativa
export const changeRectificativaTipoCobro = async (idRectificativa, tipoCobro) => {
  try {
    const response = await apiClient.post(`/facturaRectificativa/cambiar-tipo-cobro/${idRectificativa}?tipoCobro=${encodeURIComponent(tipoCobro)}`);
    return response.data;
  } catch (error) {
    console.error("Error changing rectificativa tipo de cobro:", error);
    throw error;
  }
};

// Mark honorarios as pagados
export const honorariosPagados = async (numFactura, fecha, numFacturaAdmin, confirmar = false) => {
  try {
    const requestBody = {
      numFactura: numFactura,
      fecha: fecha,
      numFacturaAdmin: numFacturaAdmin,
      confirmar: confirmar
    };
    
    try {
      console.log('[API LOG] POST', '/factura/honorariosPagados', { body: requestBody });
    } catch (logErr) {
      console.log('[API LOG] POST /factura/honorariosPagados');
    }
    
    const response = await apiClient.post('/factura/honorariosPagados', requestBody);
    return response.data;
  } catch (error) {
    console.error("Error marking honorarios as pagados:", error);
    
    // Si es un 409, la API está pidiendo confirmación - devolver los datos en lugar de error
    if (error.response && error.response.status === 409 && error.response.data) {
      return error.response.data;
    }
    
    if (error.response && error.response.data) {
      throw new Error(error.response.data.message || error.response.data.error || 'Error al marcar los honorarios como pagados');
    }
    throw error;
  }
};

//------ JOB TYPES (TIPO TRABAJO) ------//

// Get job type by ID
export const getJobTypeById = async (id) => {
  try {
    const response = await apiClient.get(`/tipoTrabajo/findByID/${id}`);
    return response.data;
  } catch (error) {
    console.error(`Error fetching job type with ID ${id}:`, error);
    throw error;
  }
};

// Create job type
export const createJobType = async (jobTypeData) => {
  try {
    const response = await apiClient.post("/tipoTrabajo/crear", jobTypeData);
    return response.data;
  } catch (error) {
    console.error("Error creating job type:", error);
    throw error;
  }
};

// Edit job type
export const editJobType = async (id, jobTypeData) => {
  try {
    const response = await apiClient.put(`/tipoTrabajo/editar/${id}`, jobTypeData);
    return response.data;
  } catch (error) {
    console.error(`Error editing job type with ID ${id}:`, error);
    throw error;
  }
};

//------ LOCALITIES (LOCALIDADES) ------//

// Get locality by ID
export const getLocalityById = async (id) => {
  try {
    const response = await apiClient.get(`/localidad/findByID/${id}`);
    return response.data;
  } catch (error) {
    console.error(`Error fetching locality with ID ${id}:`, error);
    throw error;
  }
};

// Create locality
export const createLocality = async (localityData) => {
  try {
    const response = await apiClient.post("/localidad/crear", localityData);
    return response.data;
  } catch (error) {
    console.error("Error creating locality:", error);
    throw error;
  }
};

// Edit locality
export const editLocality = async (id, localityData) => {
  try {
    const response = await apiClient.put(`/localidad/editar/${id}`, localityData);
    return response.data;
  } catch (error) {
    console.error(`Error editing locality with ID ${id}:`, error);
    throw error;
  }
};

//------ ANNOTATIONS (ANOTACIONES) ------//

// Get annotations summary by expediente ID
export const getAnnotationsSummary = async (expedienteId) => {
  try {
    const response = await apiClient.get(`/anotacion/resumen/${expedienteId}`);
    return response.data;
  } catch (error) {
    console.error(`Error fetching annotations summary for expediente ${expedienteId}:`, error);
    throw error;
  }
};

// Create note
export const createNote = async (noteData) => {
  try {
    const response = await apiClient.post("/anotacion/crearNota", noteData);
    return response.data;
  } catch (error) {
    console.error("Error creating note:", error);
    throw error;
  }
};

// Reply to email
export const replyEmail = async (emailData) => {
  try {
    const response = await apiClient.post("/anotacion/responderCorreo", emailData);
    return response.data;
  } catch (error) {
    console.error("Error replying to email:", error);
    throw error;
  }
};

//------ GMAIL ------//

// Get Gmail authorization status
export const getGmailStatus = async () => {
  try {
    const response = await apiClient.get("/gmail/status");
    return response.data;
  } catch (error) {
    console.error("Error fetching Gmail status:", error);
    throw error;
  }
};

// Get Gmail authorization URL
export const getGmailAuthUrl = async () => {
  try {
    const response = await apiClient.get("/gmail/auth-url");
    return response.data;
  } catch (error) {
    console.error("Error fetching Gmail auth URL:", error);
    throw error;
  }
};

// Get Gmail profile
export const getGmailProfile = async () => {
  try {
    const response = await apiClient.get("/gmail/profile");
    return response.data;
  } catch (error) {
    console.error("Error fetching Gmail profile:", error);
    throw error;
  }
};

//------ EMAIL ATTACHMENTS ------//

// Download email attachments by gmailMessageId
// Esta API guarda los adjuntos del email en el servidor SAMBA y devuelve la info de los archivos guardados
// expedienteId: ID del expediente donde guardar los archivos
// nombreCarpeta: nombre de la carpeta donde guardar los adjuntos
// Retorna: { savedFiles: [{ expedienteId, gmailMessageId, anotacionId, savedPath, fileName, size }] }
export const downloadEmailAttachments = async (gmailMessageId, expedienteId, nombreCarpeta) => {
  try {
    const body = {
      expedienteId,
      nombreCarpeta
    };
    
    console.log('Saving email attachments to SAMBA with params:', { gmailMessageId, body });
    
    const response = await apiClient.post(`/anotacion/descargarAdjuntos/${gmailMessageId}`, body);
    console.log('Email attachments saved to SAMBA:', response.data);
    return response.data;
  } catch (error) {
    console.error(`Error saving attachments for gmailMessageId ${gmailMessageId}:`, error);
    throw error;
  }
};

// Download single file from SAMBA path to user's browser
// The savedPath comes from downloadEmailAttachments API response
export const downloadFileFromSamba = async (savedPath, fileName) => {
  try {
    console.log('Starting download from SAMBA path:', savedPath);
    
    // Normalize the path (handle both Windows and Unix paths)
    let normalizedPath = savedPath.replace(/\\/g, '/');
    
    // Extract the relative path from the SAMBA path
    // Expected formats:
    // - /srv/samba/shared/Expedientes/YEAR/EXPEDIENTE_ID/folder/file
    // - //10.10.0.1/shared/Expedientes/YEAR/EXPEDIENTE_ID/folder/file
    const pathParts = normalizedPath.split('/').filter(part => part); // Remove empty parts
    const expedientesIndex = pathParts.indexOf('Expedientes');
    
    if (expedientesIndex === -1) {
      console.error('Invalid SAMBA path format - no "Expedientes" found:', savedPath);
      throw new Error('Invalid SAMBA path format: missing "Expedientes" folder');
    }
    
    // Get the parts after "Expedientes": [YEAR, EXPEDIENTE_ID, ...folders, filename]
    const relevantParts = pathParts.slice(expedientesIndex + 1);
    
    if (relevantParts.length < 2) {
      console.error('Invalid SAMBA path format - not enough parts after Expedientes:', savedPath);
      throw new Error('Invalid SAMBA path format: insufficient path depth');
    }
    
    const expedienteId = relevantParts[1]; // Get expediente ID
    
    // Reconstruct the relative path after expedienteId (folders + filename)
    const relativePath = relevantParts.slice(2).join('/');
    
    // Build the download URL using the same pattern as in files/index.js
    const downloadUrl = `${API_BASE_URL}/download/${expedienteId}/${encodeURIComponent(relativePath)}`;
    
    console.log('Download info:', { 
      savedPath, 
      fileName, 
      expedienteId, 
      relativePath, 
      downloadUrl 
    });
    
    // Fetch the file with authentication
    const token = localStorage.getItem('authToken');
    const response = await fetch(downloadUrl, {
      headers: token ? { 'Authorization': `Bearer ${token}` } : {}
    });
    
    if (!response.ok) {
      const errorText = await response.text();
      console.error('Download failed:', { 
        status: response.status, 
        statusText: response.statusText,
        error: errorText 
      });
      throw new Error(`Failed to download file: ${response.status} ${response.statusText}`);
    }
    
    // Create blob and trigger download
    const blob = await response.blob();
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = fileName;
    document.body.appendChild(link);
    link.click();
    
    // Cleanup
    setTimeout(() => {
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    }, 100);
    
    console.log('File downloaded successfully:', fileName);
    return { success: true, fileName };
  } catch (error) {
    console.error(`Error downloading file from SAMBA:`, error);
    throw error;
  }
};

//------ APPOINTMENTS WORKER (OPERARIOS) ------//

// Fetch pending appointments to close
export const getCitasPendientesCerrar = async () => {
  try {
    const response = await apiClient.get("/cita/citasPendientesCerrar");
    return response.data;
  } catch (error) {
    console.error("Error fetching pending appointments:", error);
    throw error;
  }
};

// Fetch upcoming appointments
export const getProximasCitas = async () => {
  try {
    const response = await apiClient.get("/cita/proximasCitas");
    return response.data;
  } catch (error) {
    console.error("Error fetching upcoming appointments:", error);
    throw error;
  }
};

// Fetch appointments history
export const getCitasHistorial = async (page = 0, size = 50) => {
  try {
    const response = await apiClient.get("/cita/citasHistorial", {
      params: { page, size }
    });
    return response.data;
  } catch (error) {
    console.error("Error fetching appointments history:", error);
    throw error;
  }
};

// Fetch appointment info for worker
export const getInfoCitaOperario = async (citaId) => {
  try {
    const response = await apiClient.get(`/cita/infoCitaOperario/${citaId}`);
    return response.data;
  } catch (error) {
    console.error("Error fetching appointment info:", error);
    throw error;
  }
};

// Upload budget to appointment
export const subirPresupuesto = async (citaId, formData) => {
  try {
    const response = await apiClient.post(
      `/cita/subirPresupuesto/${citaId}`,
      formData,
      {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      }
    );
    return response.data;
  } catch (error) {
    console.error("Error uploading budget:", error);
    throw error;
  }
};

// Upload photos to appointment
export const subirFotos = async (citaId, formData) => {
  try {
    const response = await apiClient.post(
      `/cita/subirFotos/${citaId}`,
      formData,
      {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      }
    );
    return response.data;
  } catch (error) {
    console.error("Error uploading photos:", error);
    throw error;
  }
};

// Upload comments to appointment
export const subirComentarios = async (citaId, texto) => {
  try {
    const response = await apiClient.post(
      `/cita/subirComentarios/${citaId}?texto=${encodeURIComponent(texto)}`
    );
    return response.data;
  } catch (error) {
    console.error("Error uploading comments:", error);
    throw error;
  }
};

// Upload signature to appointment
export const subirFirma = async (citaId, formData) => {
  try {
    const response = await apiClient.post(
      `/cita/subirFirma/${citaId}`,
      formData,
      {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      }
    );
    return response.data;
  } catch (error) {
    console.error("Error uploading signature:", error);
    throw error;
  }
};

// Close appointment
export const cerrarCita = async (citaId) => {
  try {
    const response = await apiClient.post(
      `/cita/cerrarCita/${citaId}`
    );
    return response.data;
  } catch (error) {
    console.error("Error closing appointment:", error);
    throw error;
  }
};

//------ FACTURACIÓN - COBROS Y HONORARIOS ------//

// Mark invoices as paid
export const darFacturaCobrado = async (numFactura, fecha, confirmar = false, marcarComoGiro = false) => {
  try {
    const response = await apiClient.post("/factura/darCobrado", {
      numFactura,
      fecha,
      confirmar,
      marcarComoGiro
    });
    return response.data;
  } catch (error) {
    console.error("Error marking invoice as paid:", error);
    
    // Si es un 409, la API está pidiendo confirmación - devolver los datos en lugar de error
    if (error.response && error.response.status === 409 && error.response.data) {
      return error.response.data;
    }
    
    if (error.response && error.response.data) {
      throw new Error(error.response.data.message || error.response.data.error || 'Error al marcar la factura como cobrada');
    }
    throw error;
  }
};

// Remove paid status from invoices
export const quitarCobrado = async (numFactura, confirmar = false) => {
  try {
    const response = await apiClient.post("/factura/quitarCobrado", {
      numFactura,
      confirmar
    });
    return response.data;
  } catch (error) {
    console.error("Error removing paid status from invoice:", error);
    
    // Si es un 409, la API está pidiendo confirmación - devolver los datos en lugar de error
    if (error.response && error.response.status === 409 && error.response.data) {
      return error.response.data;
    }
    
    if (error.response && error.response.data) {
      throw new Error(error.response.data.message || error.response.data.error || 'Error al quitar el cobro de la factura');
    }
    throw error;
  }
};

//------ LISTAS - LISTADOS GESTIÓN ------//

// Listado de Expedientes
export const getListaExpedientes = async (companiaIds = "", comercialIds = "", from = "", to = "", estados = "", tipoTrabajoIds = "") => {
  try {
    const params = new URLSearchParams();
    if (companiaIds) params.append("companiaIds", companiaIds);
    if (comercialIds) params.append("comercialIds", comercialIds);
    if (from) params.append("from", from);
    if (to) params.append("to", to);
    if (estados) params.append("estados", estados);
    if (tipoTrabajoIds) params.append("tipoTrabajoIds", tipoTrabajoIds);

    const response = await apiClient.get(`/listas/listaGestion/expedientes?${params.toString()}`, {
      responseType: 'blob'
    });
    return response.data;
  } catch (error) {
    console.error("Error downloading lista expedientes:", error);
    throw error;
  }
};

// Fecha Parte Operarios
export const getListaFechaParte = async (operarioIds = "") => {
  try {
    const params = new URLSearchParams();
    if (operarioIds) params.append("operarioIds", operarioIds);

    const response = await apiClient.get(`/listas/listaGestion/fechParte?${params.toString()}`, {
      responseType: 'blob'
    });
    return response.data;
  } catch (error) {
    console.error("Error downloading lista fecha parte:", error);
    throw error;
  }
};

//------ LISTAS - LISTADOS TÉCNICOS ------//

// Resumen Pagos por Operario
export const getListaResumenPagoOperario = async (operarioIds = "", from = "", to = "") => {
  try {
    const params = new URLSearchParams();
    if (operarioIds) params.append("operarioIds", operarioIds);
    if (from) params.append("from", from);
    if (to) params.append("to", to);

    const response = await apiClient.get(`/listas/listaTecnicos/resumenPagoOperario?${params.toString()}`, {
      responseType: 'blob'
    });
    return response.data;
  } catch (error) {
    console.error("Error downloading lista resumen pago operario:", error);
    throw error;
  }
};

// Listado Coste Reparadores
export const getListaCosteReparadores = async (from = "", to = "") => {
  try {
    const params = new URLSearchParams();
    if (from) params.append("from", from);
    if (to) params.append("to", to);

    const response = await apiClient.get(`/listas/listaTecnicos/listadoCosteReparadores?${params.toString()}`, {
      responseType: 'blob'
    });
    return response.data;
  } catch (error) {
    console.error("Error downloading lista coste reparadores:", error);
    throw error;
  }
};

//------ LISTAS - LISTADOS ADMINISTRACIÓN ------//

// Margen Bruto
export const getListaMargenBruto = async (companiaIds = "", comercialIds = "", from = "", to = "") => {
  try {
    const params = new URLSearchParams();
    if (companiaIds) params.append("companiaIds", companiaIds);
    if (comercialIds) params.append("comercialIds", comercialIds);
    if (from) params.append("from", from);
    if (to) params.append("to", to);

    const response = await apiClient.get(`/listas/listaAdmin/margenBruto?${params.toString()}`, {
      responseType: 'blob'
    });
    return response.data;
  } catch (error) {
    console.error("Error downloading lista margen bruto:", error);
    throw error;
  }
};

// Facturas Pendiente de Cobro
export const getListaFacturasPendienteCobro = async (companiaIds = "", comercialIds = "", from = "", to = "") => {
  try {
    const params = new URLSearchParams();
    if (companiaIds) params.append("companiaIds", companiaIds);
    if (comercialIds) params.append("comercialIds", comercialIds);
    if (from) params.append("from", from);
    if (to) params.append("to", to);

    const response = await apiClient.get(`/listas/listaAdmin/facturasPendienteCobro?${params.toString()}`, {
      responseType: 'blob'
    });
    return response.data;
  } catch (error) {
    console.error("Error downloading lista facturas pendiente cobro:", error);
    throw error;
  }
};

// Ratio Facturas Pendientes de Cobro
export const getListaRatioFacturasPendienteCobro = async (companiaIds = "", comercialIds = "", from = "", to = "") => {
  try {
    const params = new URLSearchParams();
    if (companiaIds) params.append("companiaIds", companiaIds);
    if (comercialIds) params.append("comercialIds", comercialIds);
    if (from) params.append("from", from);
    if (to) params.append("to", to);

    const response = await apiClient.get(`/listas/listaAdmin/ratioFacturasPendienteCobro?${params.toString()}`, {
      responseType: 'blob'
    });
    return response.data;
  } catch (error) {
    console.error("Error downloading lista ratio facturas pendiente cobro:", error);
    throw error;
  }
};

// Facturas Cobradas
export const getListaFacturasCobradas = async (companiaIds = "", comercialIds = "", from = "", to = "") => {
  try {
    const params = new URLSearchParams();
    if (companiaIds) params.append("companiaIds", companiaIds);
    if (comercialIds) params.append("comercialIds", comercialIds);
    if (from) params.append("from", from);
    if (to) params.append("to", to);

    const response = await apiClient.get(`/listas/listaAdmin/facturasCobradas?${params.toString()}`, {
      responseType: 'blob'
    });
    return response.data;
  } catch (error) {
    console.error("Error downloading lista facturas cobradas:", error);
    throw error;
  }
};

// Resumen Facturación Anual Compañías
export const getListaResumenFacturacionAnual = async (companiaIds = "", comercialIds = "", ano = "", tipoCompania = "") => {
  try {
    const params = new URLSearchParams();
    if (companiaIds) params.append("companiaIds", companiaIds);
    if (comercialIds) params.append("comercialIds", comercialIds);
    if (ano) params.append("ano", ano);
    if (tipoCompania) params.append("tipoCompania", tipoCompania);

    const response = await apiClient.get(`/listas/listaAdmin/resumenFacturacionAnual?${params.toString()}`, {
      responseType: 'blob'
    });
    return response.data;
  } catch (error) {
    console.error("Error downloading lista resumen facturacion anual:", error);
    throw error;
  }
};

// Facturación Mensual
export const getListaFacturacionMensual = async (ano = "") => {
  try {
    const params = new URLSearchParams();
    if (ano) params.append("ano", ano);

    const response = await apiClient.get(`/listas/listaAdmin/facturacionMensual?${params.toString()}`, {
      responseType: 'blob'
    });
    return response.data;
  } catch (error) {
    console.error("Error downloading lista facturacion mensual:", error);
    throw error;
  }
};

// Listado de Facturas por IVA
export const getListaFacturacionIva = async (companiaIds = "", comercialIds = "", from = "", to = "") => {
  try {
    const params = new URLSearchParams();
    if (companiaIds) params.append("companiaIds", companiaIds);
    if (comercialIds) params.append("comercialIds", comercialIds);
    if (from) params.append("from", from);
    if (to) params.append("to", to);

    const response = await apiClient.get(`/listas/listaAdmin/facturacionIva?${params.toString()}`, {
      responseType: 'blob'
    });
    return response.data;
  } catch (error) {
    console.error("Error downloading lista facturacion iva:", error);
    throw error;
  }
};

// Listado de Facturas
export const getListaFacturacion = async (companiaIds = "", comercialIds = "", from = "", to = "", nif = "") => {
  try {
    const params = new URLSearchParams();
    if (companiaIds) params.append("companiaIds", companiaIds);
    if (comercialIds) params.append("comercialIds", comercialIds);
    if (from) params.append("from", from);
    if (to) params.append("to", to);
    if (nif) params.append("nif", nif);

    const response = await apiClient.get(`/listas/listaAdmin/facturacion?${params.toString()}`, {
      responseType: 'blob'
    });
    return response.data;
  } catch (error) {
    console.error("Error downloading lista facturacion:", error);
    throw error;
  }
};

// Listado de Prefacturas
export const getListaPrefacturas = async (companiaIds = "", comercialIds = "", from = "", to = "") => {
  try {
    const params = new URLSearchParams();
    if (companiaIds) params.append("companiaIds", companiaIds);
    if (comercialIds) params.append("comercialIds", comercialIds);
    if (from) params.append("from", from);
    if (to) params.append("to", to);

    const response = await apiClient.get(`/listas/listaAdmin/prefacturas?${params.toString()}`, {
      responseType: 'blob'
    });
    return response.data;
  } catch (error) {
    console.error("Error downloading lista prefacturas:", error);
    throw error;
  }
};

// Listado 347
export const getListado347 = async (companiaIds = "", comercialIds = "", ano = "") => {
  try {
    const params = new URLSearchParams();
    if (companiaIds) params.append("companiaIds", companiaIds);
    if (comercialIds) params.append("comercialIds", comercialIds);
    if (ano) params.append("ano", ano);

    const response = await apiClient.get(`/listas/listaAdmin/listado347?${params.toString()}`, {
      responseType: 'blob'
    });
    return response.data;
  } catch (error) {
    console.error("Error downloading listado 347:", error);
    throw error;
  }
};

// Honorarios Comerciales
// Expedientes con coste y sin factura
export const getListaCosteSinFactura = async (from = "", to = "") => {
  try {
    const params = new URLSearchParams();
    if (from) params.append("from", from);
    if (to) params.append("to", to);

    const response = await apiClient.get(`/listas/listaAdmin/costeSinFactura?${params.toString()}`, {
      responseType: 'blob'
    });
    return response.data;
  } catch (error) {
    console.error("Error downloading lista coste sin factura:", error);
    throw error;
  }
};

export const getListaHonorariosComerciales = async (comercialIds = "", from = "", to = "") => {
  try {
    const params = new URLSearchParams();
    if (comercialIds) params.append("comercialIds", comercialIds);
    if (from) params.append("from", from);
    if (to) params.append("to", to);

    const response = await apiClient.get(`/listas/listaAdmin/honorariosComerciales?${params.toString()}`, {
      responseType: 'blob'
    });
    return response.data;
  } catch (error) {
    console.error("Error downloading lista honorarios comerciales:", error);
    throw error;
  }
};

// Get companies by commercial IDs (comma-separated)
// Nota: según la API, si no se envía comercialId, devuelve todas las compañías
export const getCompaniesByCommercial = async (comercialIds = "") => {
  try {
    // Si no hay comercial IDs o está vacío, el backend devuelve todas
    const params = new URLSearchParams();
    if (comercialIds && comercialIds.trim()) {
      params.append("comercialId", comercialIds);
    }
    
    const endpoint = `/compania/findByComercial${params.toString() ? '?' + params.toString() : ''}`;
    const response = await apiClient.get(endpoint);
    return response.data;
  } catch (error) {
    console.error("Error getting companies by commercial:", error);
    throw error;
  }
};

//------ ESTADÍSTICAS ------//

// Obtener estadísticas consolidadas del dashboard (expedientes, citas, facturado)
export const getEstadisticasConsolidadas = async () => {
  try {
    const response = await apiClient.get('/listas/listaAdmin/dashboardResumen');
    return response.data;
  } catch (error) {
    console.error("Error getting dashboard statistics:", error);
    throw error;
  }
};

// Obtener resumen de facturación por comerciales
export const getDashboardResumenComercial = async (from = "", to = "") => {
  try {
    const params = new URLSearchParams();
    if (from) params.append("from", from);
    if (to) params.append("to", to);

    const response = await apiClient.get(`/listas/listaAdmin/dashboardResumenComercial?${params.toString()}`);
    return response.data;
  } catch (error) {
    console.error("Error getting dashboard resumen comercial:", error);
    throw error;
  }
};

// Descargar el archivo de facturación (mismo que antes pero actualizado para usar dashboardResumenComercial como fuente de descarga)
export const downloadFacturacionComercial = async (from = "", to = "") => {
  try {
    const params = new URLSearchParams();
    if (from) params.append("from", from);
    if (to) params.append("to", to);

    const response = await apiClient.get(`/listas/listaAdmin/facturacion?${params.toString()}`, {
      responseType: 'blob'
    });
    return response.data;
  } catch (error) {
    console.error("Error downloading facturacion comercial:", error);
    throw error;
  }
};

// Lista de Gestión - Expedientes
export const getListaGestionExpedientes = async (companiaIds = "", from = "", to = "", estados = "") => {
  try {
    const params = new URLSearchParams();
    if (companiaIds) params.append("companiaIds", companiaIds);
    if (from) params.append("from", from);
    if (to) params.append("to", to);
    if (estados) params.append("estados", estados);

    const response = await apiClient.get(`/listas/listaGestion/expedientes?${params.toString()}`, {
      responseType: 'blob'
    });
    return response.data;
  } catch (error) {
    console.error("Error downloading lista expedientes:", error);
    throw error;
  }
};

// Lista de Admin - Facturación (alias para mantener compatibilidad)
export const getListaAdminFacturacion = getListaFacturacion;

// Facturación por Comercial (separada en hojas por comercial)
export const getFacturacionPorComercial = async (comercialIds = "", from = "", to = "") => {
  try {
    const params = new URLSearchParams();
    if (comercialIds) params.append("comercialIds", comercialIds);
    if (from) params.append("from", from);
    if (to) params.append("to", to);

    const response = await apiClient.get(`/listas/listaAdmin/facturacionPorComercial?${params.toString()}`, {
      responseType: 'blob'
    });
    return response.data;
  } catch (error) {
    console.error("Error downloading facturacion por comercial:", error);
    throw error;
  }
};

// Expedientes por Comercial (separados en hojas por comercial)
export const getExpedientesPorComercial = async (comercialIds = "", from = "", to = "", estados = "") => {
  try {
    const params = new URLSearchParams();
    if (comercialIds) params.append("comercialIds", comercialIds);
    if (from) params.append("from", from);
    if (to) params.append("to", to);
    if (estados) params.append("estados", estados);

    const response = await apiClient.get(`/listas/listaGestion/expedientesPorComercial?${params.toString()}`, {
      responseType: 'blob'
    });
    return response.data;
  } catch (error) {
    console.error("Error downloading expedientes por comercial:", error);
    throw error;
  }
};

//------ DEVOLVER CITA ------//

// Devolver cita por ID
export const devolverCita = async (citaId) => {
  try {
    const response = await apiClient.post(`/cita/devolverCita/${citaId}`);
    return response.data;
  } catch (error) {
    console.error("Error returning cita:", error);
    throw error;
  }
};

//------ AUSENCIAS DE OPERARIOS ------//

// Obtener todas las ausencias
export const getAusencias = async () => {
  try {
    const response = await apiClient.get("/ausencias/all");
    return response.data;
  } catch (error) {
    console.error("Error fetching ausencias:", error);
    throw error;
  }
};

// Obtener ausencias por operario
export const getAusenciasByOperario = async (operarioId) => {
  try {
    const response = await apiClient.get(`/ausencias/operario/${operarioId}`);
    return response.data;
  } catch (error) {
    console.error("Error fetching ausencias by operario:", error);
    throw error;
  }
};

// Obtener ausencias en un rango de fechas
export const getAusenciasByFechas = async (fechaInicio, fechaFin) => {
  try {
    const params = new URLSearchParams();
    if (fechaInicio) params.append("fechaInicio", fechaInicio);
    if (fechaFin) params.append("fechaFin", fechaFin);
    const response = await apiClient.get(`/ausencias/rango?${params.toString()}`);
    return response.data;
  } catch (error) {
    console.error("Error fetching ausencias by fecha range:", error);
    throw error;
  }
};

// Crear una nueva ausencia
export const createAusencia = async (ausenciaData) => {
  try {
    const response = await apiClient.post("/ausencias/crear", ausenciaData);
    return response.data;
  } catch (error) {
    console.error("Error creating ausencia:", error);
    throw error;
  }
};

// Editar una ausencia existente
export const updateAusencia = async (id, ausenciaData) => {
  try {
    const response = await apiClient.put(`/ausencias/editar/${id}`, ausenciaData);
    return response.data;
  } catch (error) {
    console.error("Error updating ausencia:", error);
    throw error;
  }
};

// Eliminar una ausencia
export const deleteAusencia = async (id) => {
  try {
    const response = await apiClient.delete(`/ausencias/eliminar/${id}`);
    return response.data;
  } catch (error) {
    console.error("Error deleting ausencia:", error);
    throw error;
  }
};
